import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  HiQuestionMarkCircle,
  HiCheck,
  HiArrowRight,
  HiPencil,
} from "react-icons/hi";
import { ChatTheme, UserQuestion } from "./types";
import useTranslation from "@/hooks/use-translation";

const CUSTOM_PREFIX = "__custom__:";

// ============================================================================
// Props
// ============================================================================

interface QuestionPromptProps {
  question: UserQuestion;
  theme: ChatTheme;
  onAnswer: (answers: Record<number, string[]>) => void;
  disabled?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Displays questions from the assistant one at a time, stepping through each.
 * Each question offers predefined options plus an "Other" free-text input.
 * Submits all answers at once on the last step.
 */
export const QuestionPrompt: React.FC<QuestionPromptProps> = ({
  question,
  theme,
  onAnswer,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const total = question.questions.length;
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selections, setSelections] = useState<Record<number, string[]>>(() => {
    const init: Record<number, string[]> = {};
    question.questions.forEach((_, idx) => {
      init[idx] = [];
    });
    return init;
  });
  const [customMode, setCustomMode] = useState<Record<number, boolean>>({});
  const [customTexts, setCustomTexts] = useState<Record<number, string>>({});
  const customInputRef = useRef<HTMLInputElement>(null);

  const currentQ = question.questions[currentIdx];
  const currentSelection = selections[currentIdx] || [];
  const isCustom = customMode[currentIdx] ?? false;
  const customText = customTexts[currentIdx] ?? "";
  const hasSelection = isCustom
    ? customText.trim().length > 0
    : currentSelection.length > 0;
  const isLast = currentIdx === total - 1;

  useEffect(() => {
    if (isCustom && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [isCustom, currentIdx]);

  const handleOptionClick = useCallback(
    (optionId: string) => {
      if (disabled) return;

      setCustomMode((prev) => ({ ...prev, [currentIdx]: false }));

      setSelections((prev) => {
        const current = prev[currentIdx] || [];
        let updated: string[];

        if (currentQ.allowMultiple) {
          updated = current.includes(optionId)
            ? current.filter((id) => id !== optionId)
            : [...current, optionId];
        } else {
          updated = [optionId];
        }

        return { ...prev, [currentIdx]: updated };
      });
    },
    [currentIdx, currentQ, disabled],
  );

  const handleCustomToggle = useCallback(() => {
    if (disabled) return;
    setCustomMode((prev) => ({ ...prev, [currentIdx]: true }));
    setSelections((prev) => ({ ...prev, [currentIdx]: [] }));
  }, [currentIdx, disabled]);

  const handleCustomTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCustomTexts((prev) => ({ ...prev, [currentIdx]: e.target.value }));
    },
    [currentIdx],
  );

  const handleNext = useCallback(() => {
    if (!hasSelection || disabled) return;

    const finalSelections = { ...selections };
    if (isCustom && customText.trim()) {
      finalSelections[currentIdx] = [`${CUSTOM_PREFIX}${customText.trim()}`];
    }

    if (isLast) {
      onAnswer(finalSelections);
    } else {
      setSelections(finalSelections);
      setCurrentIdx((prev) => prev + 1);
    }
  }, [
    hasSelection,
    disabled,
    isLast,
    onAnswer,
    selections,
    isCustom,
    customText,
    currentIdx,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && hasSelection && !disabled) {
        e.preventDefault();
        handleNext();
      }
    },
    [hasSelection, disabled, handleNext],
  );

  const isSelected = (optionId: string) => currentSelection.includes(optionId);

  return (
    <div
      className={`
        w-full max-w-[90%] rounded-xl overflow-hidden
        ${theme.assistantMsgBg} ${theme.border} border
      `}
    >
      {/* Header with step indicator */}
      <div
        className={`flex items-center justify-between px-4 py-3 ${theme.border} border-b`}
      >
        <div className="flex items-center gap-2">
          <HiQuestionMarkCircle className="h-5 w-5 text-accent flex-shrink-0" />
          <span className={`text-sm font-medium ${theme.textPrimary}`}>
            {question.title || "Question"}
          </span>
        </div>
        {total > 1 && (
          <div className="flex items-center gap-1.5">
            {question.questions.map((_, idx) => (
              <div
                key={idx}
                className={`
                  h-1.5 rounded-full transition-all duration-200
                  ${idx === currentIdx ? "w-4 bg-accent" : idx < currentIdx ? "w-1.5 bg-accent/50" : "w-1.5 bg-white/15"}
                `}
              />
            ))}
            <span className={`text-xs ${theme.textMuted} ml-1.5`}>
              {currentIdx + 1}/{total}
            </span>
          </div>
        )}
      </div>

      {/* Current question */}
      <div className="px-4 py-3">
        <p
          key={currentIdx}
          className={`text-sm ${theme.textPrimary} leading-relaxed mb-3 animate-[fadeSlideIn_0.2s_ease-out]`}
        >
          {currentQ.question}
        </p>

        <div
          key={`opts-${currentIdx}`}
          className="flex flex-col gap-2 animate-[fadeSlideIn_0.2s_ease-out]"
        >
          {/* Predefined options */}
          {currentQ.options.map((option) => (
            <button
              key={option.id}
              onClick={() => handleOptionClick(option.id)}
              disabled={disabled}
              className={`
                flex items-center gap-3 w-full px-4 py-2.5 rounded-lg text-left
                transition-all duration-150
                ${
                  isSelected(option.id) && !isCustom
                    ? "bg-accent text-white"
                    : `${theme.inputBg} ${theme.textPrimary} hover:bg-[rgba(99,102,241,0.1)]`
                }
                ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                ${theme.border} border
              `}
            >
              <div
                className={`
                  flex items-center justify-center w-5 h-5 flex-shrink-0
                  ${currentQ.allowMultiple ? "rounded" : "rounded-full"}
                  ${
                    isSelected(option.id) && !isCustom
                      ? "bg-white"
                      : `${theme.inputBg} ${theme.border} border`
                  }
                  transition-colors duration-150
                `}
              >
                {isSelected(option.id) && !isCustom && (
                  <HiCheck className="h-3.5 w-3.5 text-accent" />
                )}
              </div>
              <span className="text-sm">{option.label}</span>
            </button>
          ))}

          {/* Custom answer option */}
          {isCustom ? (
            <div
              className={`
                flex items-center gap-3 w-full px-4 py-2 rounded-lg
                bg-accent/10 ${theme.border} border border-accent/30
              `}
            >
              <HiPencil className="h-4 w-4 text-accent flex-shrink-0" />
              <input
                ref={customInputRef}
                type="text"
                value={customText}
                onChange={handleCustomTextChange}
                onKeyDown={handleKeyDown}
                placeholder="Votre réponse..."
                disabled={disabled}
                className={`
                  flex-1 bg-transparent text-sm outline-none
                  ${theme.textPrimary} placeholder:${theme.textMuted}
                `}
              />
            </div>
          ) : (
            <button
              onClick={handleCustomToggle}
              disabled={disabled}
              className={`
                flex items-center gap-3 w-full px-4 py-2.5 rounded-lg text-left
                transition-all duration-150
                ${theme.inputBg} ${theme.textMuted} hover:bg-[rgba(99,102,241,0.1)]
                ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                ${theme.border} border border-dashed
              `}
            >
              <HiPencil className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">Autre...</span>
            </button>
          )}
        </div>
      </div>

      {/* Next / Submit button */}
      <div className={`px-4 py-3 ${theme.border} border-t`}>
        <button
          onClick={handleNext}
          disabled={!hasSelection || disabled}
          className={`
            w-full py-2.5 rounded-lg text-sm font-medium
            flex items-center justify-center gap-2
            transition-all duration-150
            ${
              hasSelection && !disabled
                ? "bg-accent text-white hover:bg-accent-dark"
                : `${theme.buttonBg} ${theme.textMuted} cursor-not-allowed`
            }
          `}
        >
          {isLast ? (
            t("validate")
          ) : (
            <>
              Suivant
              <HiArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

/** Extract the display text for an answer ID, handling custom prefix. */
export function resolveAnswerLabel(
  answerId: string,
  options: Array<{ id: string; label: string }>,
): string {
  if (answerId.startsWith(CUSTOM_PREFIX)) {
    return answerId.slice(CUSTOM_PREFIX.length);
  }
  return options.find((o) => o.id === answerId)?.label ?? answerId;
}

QuestionPrompt.displayName = "QuestionPrompt";

export default QuestionPrompt;
